import React, { useState, useEffect } from 'react';
import { Lock, Plus, Minus, LogOut } from 'lucide-react';
import { products } from '../mock';
import { useInventory } from '../context/InventoryContext';
import Seo from '../components/Seo';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'vrr_admin_token';
const ATTEMPT_KEY = 'vrr_admin_attempts';
const LOCKOUT_MS = 60 * 1000;

const STATUSES = [
  { value: 'IN_STOCK',     label: 'IN STOCK',     tone: 'text-emerald-400' },
  { value: 'PREORDER',     label: 'PRE-ORDER',    tone: 'text-[#ff2fa1]' },
  { value: 'OUT_OF_STOCK', label: 'OUT OF STOCK', tone: 'text-neutral-400' },
];

const Admin = () => {
  const { getStock, getStatus, adminSet, refresh } = useInventory();
  const [authed, setAuthed] = useState(() => !!localStorage.getItem(TOKEN_KEY));
  const [pw, setPw] = useState('');
  const [edits, setEdits] = useState({});
  const [tab, setTab] = useState('inventory'); // 'inventory' | 'orders'
  const [orders, setOrders] = useState([]);
  const [orderFilter, setOrderFilter] = useState('ALL');
  const [orderQuery, setOrderQuery] = useState('');
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [openOrderRef, setOpenOrderRef] = useState(null);
  const [openOrder, setOpenOrder] = useState(null);
  const [lockedUntil, setLockedUntil] = useState(() => {
    const v = parseInt(localStorage.getItem(ATTEMPT_KEY + '_until') || '0', 10);
    return v > Date.now() ? v : 0;
  });
  const [, setTick] = useState(0);
  const [busy, setBusy] = useState(null); // "slug|label" while a row is saving

  // Verify session on mount; if the token is invalid, log out.
  useEffect(() => {
    if (!authed) return;
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setAuthed(false); return; }
    fetch(`${API}/api/admin/verify`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) { localStorage.removeItem(TOKEN_KEY); setAuthed(false); } })
      .catch(() => { /* keep session, will fail on write if broken */ });
  }, [authed]);

  useEffect(() => {
    if (!lockedUntil) return;
    const id = setInterval(() => {
      if (Date.now() >= lockedUntil) {
        setLockedUntil(0);
        localStorage.removeItem(ATTEMPT_KEY + '_until');
        localStorage.removeItem(ATTEMPT_KEY);
      } else {
        setTick(t => t + 1);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [lockedUntil]);

  const rows = [];
  for (const p of products) {
    if (p.variants && p.variants.length) {
      for (const v of p.variants) rows.push({
        slug: p.slug, name: p.name, variantLabel: v.label,
        priceDisplay: v.priceDisplay, priceIsNull: v.price === null,
      });
    } else {
      rows.push({
        slug: p.slug, name: p.name, variantLabel: null,
        priceDisplay: p.priceDisplay, priceIsNull: p.price === null,
      });
    }
  }

  const submit = async (e) => {
    e.preventDefault();
    if (lockedUntil && Date.now() < lockedUntil) {
      toast.error('Too many attempts. Try again shortly.');
      return;
    }
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pw }),
      });
      const data = await res.json();
      if (!res.ok) {
        const attempts = parseInt(localStorage.getItem(ATTEMPT_KEY) || '0', 10) + 1;
        localStorage.setItem(ATTEMPT_KEY, String(attempts));
        if (attempts >= 5) {
          const until = Date.now() + LOCKOUT_MS;
          localStorage.setItem(ATTEMPT_KEY + '_until', String(until));
          setLockedUntil(until);
          toast.error('Locked for 60 seconds.');
        } else {
          toast.error(data.detail || `Incorrect password (${5 - attempts} left)`);
        }
        setPw('');
        return;
      }
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.removeItem(ATTEMPT_KEY);
      localStorage.removeItem(ATTEMPT_KEY + '_until');
      setAuthed(true);
      setPw('');
      refresh();
    } catch (err) {
      toast.error('Network error. Please try again.');
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  // ---- Admin: Orders -------------------------------------------------------
  const loadOrders = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    setOrdersLoading(true);
    try {
      const params = new URLSearchParams();
      if (orderFilter !== 'ALL') params.set('status', orderFilter);
      if (orderQuery.trim()) params.set('q', orderQuery.trim());
      params.set('limit', '200');
      const res = await fetch(`${API}/api/admin/orders?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('fetch failed');
      const data = await res.json();
      setOrders(data.items || []);
    } catch (err) {
      toast.error('Could not load orders');
    } finally {
      setOrdersLoading(false);
    }
  };

  const openOrderDetail = async (ref) => {
    setOpenOrderRef(ref);
    setOpenOrder(null);
    const token = localStorage.getItem(TOKEN_KEY);
    try {
      const res = await fetch(`${API}/api/admin/orders/${ref}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('not found');
      setOpenOrder(await res.json());
    } catch (err) {
      toast.error('Order not found');
      setOpenOrderRef(null);
    }
  };

  const updateOrder = async (ref, patch) => {
    const token = localStorage.getItem(TOKEN_KEY);
    try {
      const res = await fetch(`${API}/api/admin/orders/${ref}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error('update failed');
      const data = await res.json();
      setOpenOrder(data);
      setOrders(prev => prev.map(o => o.ref === ref ? { ...o, ...data } : o));
      toast.success('Order updated');
    } catch (err) {
      toast.error('Could not save order');
    }
  };

  useEffect(() => {
    if (authed && tab === 'orders') loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, tab, orderFilter]);

  const saveRow = async (r, patch) => {
    const key = r.variantLabel ? `${r.slug}|${r.variantLabel}` : r.slug;
    setBusy(key);
    try {
      await adminSet(r.slug, r.variantLabel, patch);
      setEdits(e => { const c = { ...e }; delete c[key]; return c; });
      toast.success('Saved');
    } catch (err) {
      if (String(err.message).includes('Session')) {
        toast.error('Session expired — please sign in again.');
        localStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
      } else {
        toast.error(err.message || 'Save failed');
      }
    } finally {
      setBusy(null);
    }
  };

  if (!authed) {
    const isLocked = lockedUntil && Date.now() < lockedUntil;
    const secsLeft = isLocked ? Math.ceil((lockedUntil - Date.now()) / 1000) : 0;
    return (
      <main className="max-w-md mx-auto px-6 pt-24 pb-16">
        <Seo title="Restricted | Vial Rat Research Co." description="Restricted staff area." canonicalPath="/admin" noindex />
        <div className="w-14 h-14 rounded-full bg-[#ff2fa1]/10 border border-[#ff2fa1]/30 flex items-center justify-center mb-6">
          <Lock size={22} className="text-[#ff2fa1]" />
        </div>
        <div className="font-mono-label text-[11px] text-[#ff2fa1] mb-3">RESTRICTED</div>
        <h1 className="font-serif-display text-white text-4xl mb-6">Staff Access</h1>
        <form onSubmit={submit} className="space-y-6">
          <div>
            <label className="font-mono-label text-[10px] text-neutral-400 block mb-2">PASSWORD</label>
            <input type="password" value={pw} onChange={e => setPw(e.target.value)} autoFocus disabled={isLocked}
              className="w-full bg-transparent border-b border-white/15 focus:border-[#ff2fa1] outline-none py-3 text-white transition-colors disabled:opacity-50" />
          </div>
          <button type="submit" disabled={isLocked}
            className={`px-8 py-3 rounded font-mono-label text-[11px] ${isLocked ? 'bg-white/10 text-neutral-500 cursor-not-allowed' : 'btn-pink'}`}>
            {isLocked ? `LOCKED · ${secsLeft}s` : 'UNLOCK'}
          </button>
        </form>
      </main>
    );
  }

  return (
    <main className="max-w-[1200px] mx-auto px-6 md:px-10 pt-10 pb-20">
      <Seo title="Inventory | Vial Rat Research Co." description="Staff inventory control." canonicalPath="/admin" noindex />
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="font-mono-label text-[11px] text-[#ff2fa1]">STAFF · ADMIN</div>
          <h1 className="font-serif-display text-white text-4xl md:text-5xl">{tab === 'orders' ? 'Orders' : 'Inventory'}</h1>
          <p className="text-sm text-neutral-400 mt-2 max-w-2xl">
            {tab === 'orders'
              ? 'Every checkout creates a persistent order with its VR payment reference. Mark orders paid, add tracking, and keep customers up to date.'
              : "Adjust stock and status for every product and strength. Stock hitting 0 with status IN STOCK auto-flips to OUT OF STOCK. Pre-order rows stay purchasable but don't consume physical stock."}
          </p>
        </div>
        <button onClick={logout} data-testid="admin-logout-btn" className="btn-outline px-4 py-2 rounded font-mono-label text-[10px] inline-flex items-center gap-2">
          <LogOut size={12} /> LOG OUT
        </button>
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-2 mb-6 border-b border-white/10">
        {['inventory', 'orders'].map(t => (
          <button key={t}
            data-testid={`admin-tab-${t}`}
            onClick={() => setTab(t)}
            className={`px-4 py-2 font-mono-label text-[11px] -mb-px border-b-2 transition-colors ${tab === t ? 'border-[#ff2fa1] text-white' : 'border-transparent text-neutral-500 hover:text-neutral-300'}`}>
            {t === 'inventory' ? 'INVENTORY' : 'ORDERS'}
          </button>
        ))}
      </div>

      {tab === 'orders' && (
        <AdminOrders
          orders={orders}
          loading={ordersLoading}
          filter={orderFilter}
          setFilter={setOrderFilter}
          query={orderQuery}
          setQuery={setOrderQuery}
          onReload={loadOrders}
          openOrderRef={openOrderRef}
          openOrder={openOrder}
          onOpen={openOrderDetail}
          onClose={() => { setOpenOrderRef(null); setOpenOrder(null); }}
          onUpdate={updateOrder}
        />
      )}

      {tab === 'inventory' && (
      <>
      <div className="border border-white/10 rounded-lg overflow-hidden">
        <div className="hidden md:grid grid-cols-[1fr_110px_150px_260px_80px] gap-4 px-5 py-3 bg-white/[0.04] font-mono-label text-[10px] text-neutral-400 border-b border-white/10">
          <div>PRODUCT / VARIANT</div>
          <div>PRICE</div>
          <div>STATUS</div>
          <div>ADJUST STOCK</div>
          <div className="text-right">QTY</div>
        </div>
        {rows.map(r => {
          const key = r.variantLabel ? `${r.slug}|${r.variantLabel}` : r.slug;
          const serverStock = getStock(r.slug, r.variantLabel);
          const serverStatus = getStatus(r.slug, r.variantLabel);
          const editStock = edits[key]?.stock !== undefined ? edits[key].stock : serverStock;
          const editStatus = edits[key]?.status !== undefined ? edits[key].status : serverStatus;
          const dirty = String(editStock) !== String(serverStock) || editStatus !== serverStatus;
          const statusMeta = STATUSES.find(s => s.value === serverStatus) || STATUSES[0];
          const isBusy = busy === key;
          return (
            <div key={key} className="grid grid-cols-[1fr_220px] md:grid-cols-[1fr_110px_150px_260px_80px] gap-4 px-5 py-4 border-b border-white/5 last:border-none items-center">
              <div>
                <div className="text-white font-semibold text-sm">{r.name}{r.variantLabel ? ` · ${r.variantLabel}` : ''}</div>
                <div className={`font-mono-label text-[10px] md:hidden mt-0.5 ${statusMeta.tone}`}>{r.priceDisplay} · {statusMeta.label}</div>
              </div>
              <div className="hidden md:block text-sm text-neutral-300">{r.priceDisplay}</div>
              <div>
                <select
                  value={editStatus}
                  onChange={e => setEdits(x => ({ ...x, [key]: { ...(x[key] || {}), status: e.target.value } }))}
                  data-testid={`admin-status-${key}`}
                  className="bg-transparent border border-white/15 focus:border-[#ff2fa1] outline-none py-1.5 px-2 text-white text-xs rounded transition-colors w-full">
                  {STATUSES.map(s => <option key={s.value} value={s.value} className="bg-[#0a0a0b]">{s.label}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setEdits(x => ({ ...x, [key]: { ...(x[key] || {}), stock: Math.max(0, parseInt(editStock || 0, 10) - 1) } }))} disabled={parseInt(editStock || 0, 10) <= 0}
                  data-testid={`admin-dec-${key}`}
                  className="w-8 h-8 rounded border border-white/15 hover:border-[#ff2fa1] hover:text-[#ff2fa1] text-neutral-300 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed">
                  <Minus size={12} />
                </button>
                <input type="number" min="0" value={editStock}
                  onChange={e => setEdits(x => ({ ...x, [key]: { ...(x[key] || {}), stock: e.target.value } }))}
                  data-testid={`admin-input-${key}`}
                  className="w-16 bg-transparent border border-white/15 focus:border-[#ff2fa1] outline-none py-1 px-2 text-center text-white text-sm rounded transition-colors" />
                <button onClick={() => setEdits(x => ({ ...x, [key]: { ...(x[key] || {}), stock: parseInt(editStock || 0, 10) + 1 } }))}
                  data-testid={`admin-inc-${key}`}
                  className="w-8 h-8 rounded border border-white/15 hover:border-[#ff2fa1] hover:text-[#ff2fa1] text-neutral-300 flex items-center justify-center">
                  <Plus size={12} />
                </button>
                {dirty && (
                  <button onClick={() => saveRow(r, { stock: parseInt(editStock || 0, 10), status: editStatus })} disabled={isBusy}
                    data-testid={`admin-save-${key}`}
                    className="ml-1 font-mono-label text-[10px] px-3 py-1.5 rounded bg-[#ff2fa1] text-black hover:bg-[#ff5eb1] disabled:opacity-50">{isBusy ? 'SAVING…' : 'SAVE'}</button>
                )}
              </div>
              <div className="hidden md:block text-right text-white text-lg tabular-nums">{serverStock}</div>
            </div>
          );
        })}
      </div>

      <p className="mt-8 text-xs text-neutral-500 font-mono-label">Manual changes here persist in the database. Deploys, redeploys and restarts never overwrite these values — only your edits and successful customer orders change stock.</p>
      </>
      )}
    </main>
  );
};

// ---------------------------------------------------------------------------
// AdminOrders component
// ---------------------------------------------------------------------------
const STATUS_META = {
  AWAITING_PAYMENT:                   { label: 'AWAITING PAYMENT',                      tone: 'text-amber-400 border-amber-500/40 bg-amber-500/10' },
  PAYMENT_SENT_AWAITING_VERIFICATION: { label: 'PAYMENT SENT — AWAITING VERIFICATION',  tone: 'text-sky-400 border-sky-500/40 bg-sky-500/10' },
  PAID:             { label: 'PAID',             tone: 'text-emerald-400 border-emerald-500/40 bg-emerald-500/10' },
  PROCESSING:       { label: 'PROCESSING',       tone: 'text-sky-400 border-sky-500/40 bg-sky-500/10' },
  SHIPPED:          { label: 'SHIPPED',          tone: 'text-[#ff2fa1] border-[#ff2fa1]/40 bg-[#ff2fa1]/10' },
  CANCELLED:        { label: 'CANCELLED',        tone: 'text-neutral-400 border-white/15 bg-white/5' },
};
const ORDER_STATUSES = ['AWAITING_PAYMENT', 'PAYMENT_SENT_AWAITING_VERIFICATION', 'PAID', 'PROCESSING', 'SHIPPED', 'CANCELLED'];

const AdminOrders = ({ orders, loading, filter, setFilter, query, setQuery, onReload, openOrderRef, openOrder, onOpen, onClose, onUpdate }) => (
  <div data-testid="admin-orders-panel">
    <div className="flex flex-wrap gap-2 mb-4">
      <input value={query} onChange={e => setQuery(e.target.value)}
        placeholder="Search by VR-REF, name or email…"
        data-testid="admin-orders-search"
        className="flex-1 min-w-[200px] bg-transparent border border-white/15 rounded px-3 py-2 text-sm text-white placeholder-neutral-500 focus:border-[#ff2fa1] outline-none" />
      <select value={filter} onChange={e => setFilter(e.target.value)}
        data-testid="admin-orders-filter"
        className="bg-[#0a0a0b] border border-white/15 rounded px-3 py-2 text-sm text-white">
        <option value="ALL">All statuses</option>
        {ORDER_STATUSES.map(s => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
      </select>
      <button onClick={onReload} data-testid="admin-orders-reload" className="btn-outline px-3 py-2 rounded font-mono-label text-[10px]">RELOAD</button>
    </div>

    {loading && <p className="text-sm text-neutral-500">Loading orders…</p>}
    {!loading && orders.length === 0 && <p className="text-sm text-neutral-500">No orders yet.</p>}

    <div className="border border-white/10 rounded-lg overflow-hidden">
      {orders.map(o => {
        const m = STATUS_META[o.status] || STATUS_META.AWAITING_PAYMENT;
        return (
          <button key={o.ref} onClick={() => onOpen(o.ref)}
            data-testid={`admin-order-row-${o.ref}`}
            className="w-full text-left px-5 py-4 border-b border-white/5 last:border-none hover:bg-white/[0.03] grid grid-cols-[1fr_auto] md:grid-cols-[180px_1fr_120px_160px] gap-3 items-center">
            <div className="font-mono-label text-[11px] text-[#ff2fa1]">{o.ref}</div>
            <div className="text-sm text-white truncate">
              {(o.customer?.firstName || '') + ' ' + (o.customer?.lastName || '')}
              <span className="text-neutral-500"> · {o.customer?.email || ''}</span>
            </div>
            <div className="hidden md:block text-sm text-neutral-300 tabular-nums">${(o.total || 0).toFixed(2)}</div>
            <div className={`font-mono-label text-[10px] px-2 py-1 border rounded w-fit ${m.tone}`}>{m.label}</div>
          </button>
        );
      })}
    </div>

    {openOrderRef && (
      <OrderDetailModal ref_={openOrderRef} order={openOrder} onClose={onClose} onUpdate={onUpdate} />
    )}
  </div>
);

const OrderDetailModal = ({ ref_, order, onClose, onUpdate }) => {
  const [status, setStatus] = useState(order?.status || 'AWAITING_PAYMENT');
  const [tracking, setTracking] = useState(order?.tracking_number || '');
  const [carrier, setCarrier] = useState(order?.tracking_carrier || '');
  const [notes, setNotes] = useState(order?.internal_notes || '');
  useEffect(() => {
    if (order) {
      setStatus(order.status || 'AWAITING_PAYMENT');
      setTracking(order.tracking_number || '');
      setCarrier(order.tracking_carrier || '');
      setNotes(order.internal_notes || '');
    }
  }, [order?.ref]);
  if (!order) {
    return (
      <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-[#111114] border border-white/10 rounded-lg p-8 text-neutral-400">Loading {ref_}…</div>
      </div>
    );
  }
  const c = order.customer || {};
  const fulfillment = order.fulfillment || 'SHIP';
  return (
    <div className="fixed inset-0 bg-black/80 z-50 overflow-y-auto p-4" onClick={onClose}>
      <div className="max-w-3xl mx-auto bg-[#111114] border border-white/10 rounded-lg my-10" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <div className="font-mono-label text-[10px] text-[#ff2fa1]">PAYMENT REFERENCE</div>
            <div className="font-mono-label text-xl text-white tracking-wider" data-testid="order-detail-ref">{order.ref}</div>
          </div>
          <button onClick={onClose} data-testid="order-detail-close" className="text-neutral-400 hover:text-white font-mono-label text-[10px]">CLOSE ×</button>
        </div>
        <div className="p-6 grid md:grid-cols-2 gap-6">
          <div>
            <div className="font-mono-label text-[10px] text-neutral-400 mb-2">CUSTOMER</div>
            <div className="text-white text-sm">{c.firstName} {c.lastName}</div>
            <div className="text-neutral-400 text-sm">{c.email}</div>
            {c.phone && <div className="text-neutral-400 text-sm">{c.phone}</div>}
            <div className="font-mono-label text-[10px] text-neutral-400 mt-4 mb-2">{fulfillment === 'LOCAL_PICKUP' ? 'LOCAL PICKUP' : 'SHIPPING ADDRESS'}</div>
            {fulfillment === 'LOCAL_PICKUP' ? (
              <div className="text-neutral-400 text-sm">Arranged privately by email after confirmation.</div>
            ) : (
              <div className="text-neutral-300 text-sm">
                {c.address}<br />{c.city}, {c.province} {c.postal}
              </div>
            )}
            {c.notes && <div className="text-neutral-400 text-xs mt-3 italic">"{c.notes}"</div>}
          </div>
          <div>
            <div className="font-mono-label text-[10px] text-neutral-400 mb-2">STATUS</div>
            {(order.status === 'AWAITING_PAYMENT' || order.status === 'PAYMENT_SENT_AWAITING_VERIFICATION') && (
              <button
                type="button"
                onClick={() => onUpdate(order.ref, { status: 'PAID' })}
                data-testid="order-detail-mark-paid"
                className="w-full mb-3 py-3 rounded font-mono-label text-[11px] bg-emerald-500 text-black hover:bg-emerald-400 inline-flex items-center justify-center gap-2"
              >
                ✓ MARK AS PAID
              </button>
            )}
            <select value={status} onChange={e => setStatus(e.target.value)}
              data-testid="order-detail-status"
              className="w-full bg-[#0a0a0b] border border-white/15 rounded px-3 py-2 text-sm text-white mb-3">
              {ORDER_STATUSES.map(s => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
            </select>
            <div className="font-mono-label text-[10px] text-neutral-400 mb-1">TRACKING NUMBER</div>
            <input value={tracking} onChange={e => setTracking(e.target.value)}
              data-testid="order-detail-tracking"
              placeholder="e.g. 1Z…"
              className="w-full bg-transparent border border-white/15 rounded px-3 py-2 text-sm text-white mb-2 focus:border-[#ff2fa1] outline-none" />
            <input value={carrier} onChange={e => setCarrier(e.target.value)}
              placeholder="Carrier (Canada Post, UPS…)"
              className="w-full bg-transparent border border-white/15 rounded px-3 py-2 text-sm text-white mb-3 focus:border-[#ff2fa1] outline-none" />
            <div className="font-mono-label text-[10px] text-neutral-400 mb-1">INTERNAL NOTES</div>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
              className="w-full bg-transparent border border-white/15 rounded px-3 py-2 text-sm text-white mb-3 focus:border-[#ff2fa1] outline-none" />
            <button data-testid="order-detail-save"
              onClick={() => onUpdate(order.ref, { status, tracking_number: tracking, tracking_carrier: carrier, internal_notes: notes })}
              className="btn-pink px-5 py-2 rounded font-mono-label text-[11px] w-full">SAVE</button>
          </div>
        </div>
        <div className="px-6 pb-6">
          <div className="font-mono-label text-[10px] text-neutral-400 mb-2">ITEMS</div>
          <div className="border border-white/10 rounded">
            {(order.items || []).map((it, idx) => (
              <div key={idx} className="flex items-center justify-between px-4 py-3 border-b border-white/5 last:border-none text-sm">
                <div>
                  <div className="text-white">{it.name || it.slug} <span className="text-neutral-500">{it.variant_label || it.dose || ''}</span></div>
                  <div className="font-mono-label text-[10px] text-neutral-500">QTY {it.qty} · ${(it.price || 0).toFixed(2)} each</div>
                </div>
                <div className="text-white tabular-nums">${((it.price || 0) * (it.qty || 0)).toFixed(2)}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 space-y-1 text-sm">
            <div className="flex justify-between text-neutral-300"><span>Subtotal</span><span className="tabular-nums">${(order.subtotal || 0).toFixed(2)}</span></div>
            {(order.discounts || []).map((d, i) => (
              <div key={i} className="flex justify-between text-[#ff2fa1]"><span className="pr-3 leading-snug">{d.description}</span><span className="tabular-nums">− ${(d.amount || 0).toFixed(2)}</span></div>
            ))}
            <div className="flex justify-between text-neutral-300"><span>{fulfillment === 'LOCAL_PICKUP' ? 'Local Pickup — FREE' : 'Shipping (Canada, tracked)'}</span><span className="tabular-nums">${(order.shipping || 0).toFixed(2)}</span></div>
            <div className="flex justify-between pt-2 border-t border-white/10 text-white font-semibold"><span>Total (CAD)</span><span className="tabular-nums" data-testid="order-detail-total">${(order.total || 0).toFixed(2)}</span></div>
            <div className="pt-2 text-xs text-neutral-500">Payment method: {order.method || 'INTERAC_ETRANSFER'} · Created {new Date(order.created_at || Date.now()).toLocaleString()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
